/**
 * useAudioCapture
 *
 * マイクからPCM16音声を取得し、base64エンコードして
 * コールバック(onAudioData)に渡すフック。
 *
 * OpenAI Realtime APIが要求するフォーマット:
 *   - サンプルレート: 24000 Hz
 *   - チャンネル数: 1 (モノラル)
 *   - フォーマット: PCM16 (Int16) → base64
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { t } from '../i18n';

export interface AudioDevice {
  deviceId: string;
  label: string;
}

interface UseAudioCaptureOptions {
  onAudioData: (base64: string) => void;
  onError?: (err: Error) => void;
  onDeviceDisconnected?: () => void;
}

interface UseAudioCaptureReturn {
  devices: AudioDevice[];
  selectedDeviceId: string | null;
  audioLevel: number; // 0.0 - 1.0
  isCapturing: boolean;
  isMuted: boolean;
  enumerateDevices: () => Promise<void>;
  startCapture: (deviceId?: string) => Promise<void>;
  stopCapture: () => void;
  setMuted: (muted: boolean) => void;
  selectDevice: (deviceId: string) => void;
}

const SAMPLE_RATE = 24000;
const BUFFER_SIZE = 4096; // ~170ms per chunk at 24kHz

// Float32 → Int16 変換
function float32ToInt16(buffer: Float32Array): Int16Array {
  const out = new Int16Array(buffer.length);
  for (let i = 0; i < buffer.length; i++) {
    const s = Math.max(-1, Math.min(1, buffer[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

// ArrayBuffer → base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// RMS音量計算（0.0〜1.0）
function calcRMS(buffer: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < buffer.length; i++) {
    sum += buffer[i] * buffer[i];
  }
  return Math.min(1, Math.sqrt(sum / buffer.length) * 5);
}

export function useAudioCapture({
  onAudioData,
  onError,
  onDeviceDisconnected,
}: UseAudioCaptureOptions): UseAudioCaptureReturn {
  const [devices, setDevices] = useState<AudioDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isMuted, setIsMutedState] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const isMutedRef = useRef(false);
  const isCapturingRef = useRef(false);

  // デバイス一覧取得
  const enumerateDevices = useCallback(async () => {
    try {
      // ラベル取得のために一時的に権限を要求
      await navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
        s.getTracks().forEach((t) => t.stop());
      });

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || t(`マイク (${d.deviceId.slice(0, 8)})`, `Microphone (${d.deviceId.slice(0, 8)})`),
        }));

      setDevices(audioInputs);

      if (audioInputs.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(audioInputs[0].deviceId);
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(t('デバイス一覧の取得に失敗しました', 'Could not list audio devices')));
    }
  }, [selectedDeviceId, onError]);

  // キャプチャ開始
  const startCapture = useCallback(
    async (deviceId?: string) => {
      const targetDeviceId = deviceId ?? selectedDeviceId;

      // 既存のキャプチャを停止
      stopCapture();

      try {
        const constraints: MediaStreamConstraints = {
          audio: {
            deviceId: targetDeviceId ? { exact: targetDeviceId } : undefined,
            sampleRate: SAMPLE_RATE,
            channelCount: 1,
            echoCancellation: false, // セミナー音声はエコーキャンセル不要
            noiseSuppression: false,
            autoGainControl: false,
          },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        // AudioContext 作成（24kHz）
        const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
        audioContextRef.current = ctx;

        const source = ctx.createMediaStreamSource(stream);
        sourceRef.current = source;

        // AudioWorkletNode（ScriptProcessorNodeの後継）
        const workletCode = `
class PCMProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._buffer = new Float32Array(${BUFFER_SIZE});
    this._offset = 0;
  }
  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) return true;
    const chunk = input[0];
    for (let i = 0; i < chunk.length; i++) {
      this._buffer[this._offset++] = chunk[i];
      if (this._offset >= ${BUFFER_SIZE}) {
        this.port.postMessage(this._buffer.slice(0));
        this._offset = 0;
      }
    }
    return true;
  }
}
registerProcessor('pcm-processor', PCMProcessor);
`;
        const blob = new Blob([workletCode], { type: 'application/javascript' });
        const workletUrl = URL.createObjectURL(blob);
        await ctx.audioWorklet.addModule(workletUrl);
        URL.revokeObjectURL(workletUrl);

        const workletNode = new AudioWorkletNode(ctx, 'pcm-processor');
        processorRef.current = workletNode;

        workletNode.port.onmessage = (e: MessageEvent<Float32Array>) => {
          const inputData = e.data;

          // 音量メーター更新
          setAudioLevel(calcRMS(inputData));

          // ミュート中はデータを送らない
          if (isMutedRef.current) return;

          // Float32 → Int16 → base64
          const int16 = float32ToInt16(inputData);
          const base64 = arrayBufferToBase64(int16.buffer as ArrayBuffer);
          onAudioData(base64);
        };

        source.connect(workletNode);
        workletNode.connect(ctx.destination);

        // デバイス切断検知
        stream.getAudioTracks()[0].addEventListener('ended', () => {
          if (isCapturingRef.current) {
            onDeviceDisconnected?.();
            stopCapture();
            setIsCapturing(false);
          }
        });

        isCapturingRef.current = true;
        setIsCapturing(true);

        if (targetDeviceId) {
          setSelectedDeviceId(targetDeviceId);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(t('マイクを起動できませんでした', 'Could not start the microphone'));
        onError?.(error);
      }
    },
    [selectedDeviceId, onAudioData, onError, onDeviceDisconnected]
  );

  // キャプチャ停止
  const stopCapture = useCallback(() => {
    isCapturingRef.current = false;

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.port.onmessage = null;
      processorRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    setIsCapturing(false);
    setAudioLevel(0);
  }, []);

  // ミュート切り替え
  const setMuted = useCallback((muted: boolean) => {
    isMutedRef.current = muted;
    setIsMutedState(muted);
    if (muted) setAudioLevel(0);
  }, []);

  const selectDevice = useCallback((deviceId: string) => {
    setSelectedDeviceId(deviceId);
  }, []);

  // デバイス変更の監視
  useEffect(() => {
    const handler = () => {
      enumerateDevices();
    };
    navigator.mediaDevices.addEventListener('devicechange', handler);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handler);
    };
  }, [enumerateDevices]);

  // アンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      stopCapture();
    };
  }, [stopCapture]);

  return {
    devices,
    selectedDeviceId,
    audioLevel,
    isCapturing,
    isMuted,
    enumerateDevices,
    startCapture,
    stopCapture,
    setMuted,
    selectDevice,
  };
}
